import {runInAction} from 'mobx';
import {GuestController, IGuestController} from '../../components/guest/controller';
import {getRootStore} from '../../app.bootstrap';
import {LOG_EVENTS} from '@techsee/techsee-common/lib/constants/event-logs.constants';
import {MeetingMode} from '@techsee/techsee-common/lib/constants/room.constants';
import {MAX_ACTIVE_PARTICIPANTS} from '@techsee/techsee-common/lib/constants/guest.constants';
import {InvitationEndedReason} from '../invitation-ended/controller';
import {NwayParticipantState} from './NwayParticipantState';
import {ITsReactStateHelper} from '../../services/ts-react-state-helper/ts-react-state-helper.service';
import {RouteName} from '../../router/route-names';
import {ParticipantData} from './participant-state.module';

export class ParticipantController {
    public guestController: IGuestController | undefined;

    constructor(
        private readonly _state: NwayParticipantState,
        private readonly _stateHelper: ITsReactStateHelper
    ) {
        this.joinSession = this.joinSession.bind(this);
    }

    init(): Promise<void> {
        const rootStore = getRootStore();

        return rootStore.localizationService.init().then(() => {
            const translate = rootStore.localizationService.translate;

            this.guestController = new GuestController(
                translate,
                rootStore.domUtilsService,
                undefined,
                this.joinSession,
                {ended: false, isExpired: false}
            );

            return rootStore.generalApiService
                .getNWay(this._state.guid)
                .then(async (details: any) => {
                    runInAction(() => {
                        if (details?.isSalesforce || details.session?.deepLink) {
                            this.guestController!.isSf = true;
                            this.guestController!.isMini = true;
                            this.guestController!.isEmb = true;
                        }
                        this.guestController!.isReady = true;
                    });

                    if (details.participants.length >= MAX_ACTIVE_PARTICIPANTS) {
                        return await this._stateHelper.navigateTo(RouteName.INVITATION_ENDED, {
                            err: true,
                            reason: InvitationEndedReason.guestLimitExceeded
                        });
                    }
                })
                .catch(async (err: any) => {
                    if (err instanceof Response) {
                        throw err;
                    }

                    runInAction(() => {
                        this.guestController!.isReady = true;
                    });

                    rootStore.eventsLogService.error({
                        logType: LOG_EVENTS.guestObserverDetailsFailed.type,
                        meta: {
                            guid: this._state.guid,
                            error: `failed GET participants/details using guid: ${this._state.guid}, with error: ${err}`
                        }
                    });

                    return await this._stateHelper.navigateTo(RouteName.INVITATION_ENDED, {
                        err: true,
                        reason: InvitationEndedReason.sessionExpired
                    });
                });
        });
    }

    joinSession(firstName: string, lastName: string): Promise<void> {
        const rootStore = getRootStore();
        const guid = this._state.guid;

        if (this.guestController) {
            this.guestController.isMini = this.guestController.isMini || this._state.isMini;
            this.guestController.isEmb = this.guestController.isEmb || this._state.isEmb;
        }

        return rootStore.generalApiService
            .nwayLogin(guid, {firstName, lastName})
            .then(async (res: any) => {
                const participantData: ParticipantData = {
                    id: res.participantId,
                    firstName,
                    lastName,
                    isSf: this.guestController?.isSf || false
                };

                rootStore.browserUtilsService.saveToSessionStorage('nway-participant', participantData);
                rootStore.authService.setAuthToken(res);

                return this._stateHelper.navigateTo(RouteName.INVITE, {
                    g: guid,
                    observe: true,
                    sw: MeetingMode.faceMeet.toLowerCase(),
                    ...(this.guestController?.isMini && {mini: this.guestController.isMini}),
                    ...(this.guestController?.isEmb && {emb: this.guestController.isEmb})
                });
            })
            .catch(async (err: any) => {
                rootStore.eventsLogService.error({
                    logType: LOG_EVENTS.guestObserverDetailsFailed.type,
                    meta: {
                        guid,
                        error: `Failed login of participant ${firstName} ${lastName}, with error: ${err}`
                    }
                });

                const isRoomFull =
                    err?.response?.data?.code === 'GUEST_LIMIT_EXCEEDED' || err?.response?.status === 409;
                const reason = isRoomFull
                    ? InvitationEndedReason.guestLimitExceeded
                    : InvitationEndedReason.sessionExpired;

                return await this._stateHelper.navigateTo(RouteName.INVITATION_ENDED, {
                    err: true,
                    reason
                });
            });
    }
}
